import { GET, POST } from "@/core/http/methods";
import { ActionContext, ActionTree } from "vuex";
import { LoginFields } from "@/modules/login/components/forms/login/login-models";
import {
  ChangePasswordRequestData,
  ChangePasswordResponseData,
  LoginRequestData,
  LoginResponseData,
  Status,
} from "@/modules/login/models/api";
import { ChangePasswordFields } from "@/modules/login/components/forms/change-password/change-password-models";
import { RootState } from "@/store/state";
import router from "@/router/router";

import { log } from "@/core/logger";
import { LoginPageState } from "@/modules/login/store/state";
import { RootMutations } from "@/store/mutations";
import { LoginActions, LoginMutations } from "./types";

import store from "@/store";
import { EventBus } from "@/eventBus";

type Context = ActionContext<LoginPageState, RootState>;

const actions: ActionTree<LoginPageState, RootState> = {
  /** Login **/

  async [LoginActions.LOGIN](context: Context, payload: LoginFields): Promise<void> {
    const requestBody: LoginRequestData = {
      user: payload.userName,
      password: payload.password,
      remember: true,
    };
    try {
      log(`Attempting to login user ${payload.userName}`);
      await POST("auth/login_check", requestBody);
      return await context.dispatch(LoginActions.LOGIN_SUCCESS, {
        userName: payload.userName,
      });
    } catch (error: any) {
      log(`Failed to login user ${payload.userName}`);
      return await context.dispatch(LoginActions.LOGIN_FAILURE, {
        userName: requestBody.user,
        responseData: error.response.data,
      });
    }
  },

  [LoginActions.LOGIN_SUCCESS](context: Context, payload: { userName: string }): void {
    log(`Successfully logged in user: ${payload.userName}`);
    context.commit(
      RootMutations.SET_LOGGED_IN_USERNAME,
      {
        userName: payload.userName,
      },
      { root: true }
    );
    log(`Navigating to the main app`);
    const urlParams = store.state.queryParams?.toString();
    if (urlParams) {
      router.push(`/?${urlParams}`);
    } else {
      router.push("/");
    }
  },

  [LoginActions.LOGIN_FAILURE](
    context: Context,
    payload: {
      userName: string;
      responseData: LoginResponseData;
    }
  ): void {
    switch (payload.responseData.status) {
      case Status.Locked:
        context.commit(LoginMutations.SET_LOCKED_MESSAGE, {
          failedLoginTimeout: payload.responseData["failed-login-timeout-min"],
        });
        context.commit(LoginMutations.SET_ERROR_MESSAGE, {
          status: payload.responseData.status,
        });
        break;
      case Status.PasswordExpired:
        context.commit(LoginMutations.DELETE_ERROR_MESSAGE);
        context.commit(
          RootMutations.SET_LOGGED_IN_USERNAME,
          { userName: payload.userName },
          { root: true }
        );
        context.commit(LoginMutations.SET_PASSWORD_EXPIRED, {
          passwordPolicy: payload.responseData["password-policy"],
        });
        break;
      case Status.Deactivated:
      case Status.PermanentlyLocked:
      case Status.Failed:
        context.commit(LoginMutations.SET_ERROR_MESSAGE, {
          status: payload.responseData.status,
        });
        break;
      default:
        log(`Unknown error while trying to login. Response data: ${payload.responseData}`);
    }
  },

  /** Change Password **/

  async [LoginActions.CHANGE_PASSWORD](
    context: Context,
    payload: ChangePasswordFields
  ): Promise<void> {
    const userName = context.rootState.loggedInUserName;
    const requestBody: ChangePasswordRequestData = {
      username: userName,
      old_password: payload.old,
      new_password: payload.current,
      verify_password: payload.confirm,
    };
    try {
      log(`Attempting to change password for user: ${userName}`);
      await POST("auth/change_password", requestBody);
      return await context.dispatch(LoginActions.CHANGE_PASSWORD_SUCCESS, {
        userName,
      });
    } catch (error: any) {
      console.log(error);
      log(`Failed to change password for user: ${userName}`);
      const responseData = error.response.data;
      return await context.dispatch(LoginActions.CHANGE_PASSWORD_FAILURE, { responseData });
    }
  },

  [LoginActions.CHANGE_PASSWORD_SUCCESS](context: Context, payload: { userName: string }): void {
    context.commit(
      RootMutations.SET_LOGGED_IN_USERNAME,
      {
        userName: payload.userName,
      },
      { root: true }
    );
    log(`re-routing to the login page`);
    window.location.reload();
  },

  [LoginActions.CHANGE_PASSWORD_FAILURE](
    context: Context,
    payload: { responseData: ChangePasswordResponseData }
  ): void {
    context.commit(LoginMutations.SET_ERROR_MESSAGE, {
      status: payload.responseData.status,
    });
  },

  /** Logout **/

  async [LoginActions.LOGOUT](context: Context) {
    await GET("auth/logout");
    // TODO: Clear canvas (maybe reset entire state?)
    EventBus.emit("event-bridge-api", {
      eventName: "clear-canvas-from-vue",
    });
    context.commit(RootMutations.REMOVE_CURRENT_CASE, {}, { root: true });
    context.commit(RootMutations.CLEAR_QUERY_PARAMS, {}, { root: true });
    context.commit("removeCurrentSlide", {}, { root: true });
    context.commit("clearAllHeatmaps", {}, { root: true });
    await router.push("/login");
    // TODO (Matan Narkiss): Remove this reload once we fix our state issues regarding POI Gallery and Viewer
    window.location.reload();
  },
};

export default actions;
